import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, CheckCircle, AlertCircle, Edit, X, Lock, CreditCard } from 'lucide-react';
import studyAbroadService from '../../services/studyAbroadService';
import { countries } from '../../data/countries';
import PaymentModal from './PaymentModal';

const ProgrammePreferencesSection = ({ profile, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentBanner, setShowPaymentBanner] = useState(false);
  const [formData, setFormData] = useState({
    preferredCountries: [],
    preferredCities: [],
    levelOfStudy: '',
    fieldOfStudy: '',
    programmeChoices: [], // Array of {institution, programme}
    intakePreference: '',
    studyMode: '',
    budgetRange: '',
    scholarshipInterest: false,
    accommodationNeeded: false,
    additionalPreferences: ''
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Temporary input states for multi-select fields
  const [countryInput, setCountryInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [newProgrammeChoice, setNewProgrammeChoice] = useState({
    institution: '',
    programme: ''
  });

  useEffect(() => {
    if (profile?.programmePreferences) {
      const prefs = profile.programmePreferences;
      const hasData = prefs.levelOfStudy || prefs.fieldOfStudy || (prefs.preferredCountries && prefs.preferredCountries.length > 0);
      
      setFormData({
        preferredCountries: prefs.preferredCountries || [],
        preferredCities: prefs.preferredCities || [],
        levelOfStudy: prefs.levelOfStudy || '',
        fieldOfStudy: prefs.fieldOfStudy || '',
        programmeChoices: prefs.programmeChoices || [],
        intakePreference: prefs.intakePreference || '',
        studyMode: prefs.studyMode || '',
        budgetRange: prefs.budgetRange || '',
        scholarshipInterest: prefs.scholarshipInterest || false,
        accommodationNeeded: prefs.accommodationNeeded || false,
        additionalPreferences: prefs.additionalPreferences || ''
      });
      
      setIsEditing(!hasData);
    } else {
      setIsEditing(true);
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addCountry = () => {
    if (countryInput && !formData.preferredCountries.includes(countryInput)) {
      setFormData(prev => ({
        ...prev,
        preferredCountries: [...prev.preferredCountries, countryInput]
      }));
      setCountryInput('');
    }
  };

  const removeCountry = (country) => {
    setFormData(prev => ({
      ...prev,
      preferredCountries: prev.preferredCountries.filter(c => c !== country)
    }));
  };

  const addCity = () => {
    if (cityInput && !formData.preferredCities.includes(cityInput)) {
      setFormData(prev => ({
        ...prev,
        preferredCities: [...prev.preferredCities, cityInput]
      }));
      setCityInput('');
    }
  };

  const removeCity = (city) => {
    setFormData(prev => ({
      ...prev,
      preferredCities: prev.preferredCities.filter(c => c !== city)
    }));
  };

  const addProgrammeChoice = () => {
    if (newProgrammeChoice.institution && newProgrammeChoice.programme) {
      setFormData(prev => ({
        ...prev,
        programmeChoices: [...prev.programmeChoices, { ...newProgrammeChoice }]
      }));
      setNewProgrammeChoice({ institution: '', programme: '' });
    }
  };

  const removeProgrammeChoice = (index) => {
    setFormData(prev => ({
      ...prev,
      programmeChoices: prev.programmeChoices.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await studyAbroadService.updateProgrammePreferences(formData);
      setSuccessMessage('Programme preferences saved successfully!');
      setIsEditing(false);
      if (onRefresh) onRefresh();
      
      // Check if user has completed all free sections and hasn't paid
      const hasCompletedFreeSection = 
        profile?.sectionsCompleted?.personalDetails &&
        profile?.sectionsCompleted?.academicHistory;
      
      if (hasCompletedFreeSection && profile?.paymentStatus !== 'paid') {
        setShowPaymentBanner(true);
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to save programme preferences');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profile?.programmePreferences) {
      const prefs = profile.programmePreferences;
      setFormData({
        preferredCountries: prefs.preferredCountries || [],
        preferredCities: prefs.preferredCities || [],
        levelOfStudy: prefs.levelOfStudy || '',
        fieldOfStudy: prefs.fieldOfStudy || '',
        programmeChoices: prefs.programmeChoices || [],
        intakePreference: prefs.intakePreference || '',
        studyMode: prefs.studyMode || '',
        budgetRange: prefs.budgetRange || '',
        scholarshipInterest: prefs.scholarshipInterest || false,
        accommodationNeeded: prefs.accommodationNeeded || false,
        additionalPreferences: prefs.additionalPreferences || ''
      });
    }
    setIsEditing(false);
    setErrorMessage('');
  };

  const formatLevelOfStudy = (level) => {
    if (!level) return 'Not provided';
    return level.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatStudyMode = (mode) => {
    if (!mode) return 'Not provided';
    return mode.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header with Edit Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#202356] mb-2">Programme Preferences</h1>
            <p className="text-gray-600">
              {isEditing ? 'Tell us about your study preferences and goals' : 'Your study preferences'}
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 border border-[#202356] text-[#202356] rounded-lg font-medium hover:bg-[#202356] hover:text-white transition-colors"
            >
              <Edit size={18} />
              Edit
            </button>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-green-700">{successMessage}</p>
          </motion.div>
        )}

        {/* Payment Banner - Shows after completing all free sections */}
        {showPaymentBanner && profile?.paymentStatus !== 'paid' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-6 text-white relative"
          >
            <button
              onClick={() => setShowPaymentBanner(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-200"
            >
              <X size={20} />
            </button>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">🎉 Great! You've Completed All Free Sections</h3>
                <p className="text-sm text-blue-100 mb-4">
                  Ready to take the next step? Unlock Documents and SOP sections to continue your application journey. 
                  We'll handle your complete application from start to finish.
                </p>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="bg-white text-[#202356] font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <CreditCard size={20} />
                  <span>Unlock Now - Pay $149</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </motion.div>
        )}

        {/* View Mode */}
        {!isEditing ? (
          <div className="space-y-6">
            {/* Study Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Study Preferences</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Level of Study</p>
                  <p className="text-base font-medium text-gray-900">{formatLevelOfStudy(formData.levelOfStudy)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Field of Study</p>
                  <p className="text-base font-medium text-gray-900">{formData.fieldOfStudy || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Study Mode</p>
                  <p className="text-base font-medium text-gray-900">{formatStudyMode(formData.studyMode)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Intake Preference</p>
                  <p className="text-base font-medium text-gray-900 capitalize">{formData.intakePreference || 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Location Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Location Preferences</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Preferred Countries</p>
                  {formData.preferredCountries && formData.preferredCountries.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.preferredCountries.map((country, index) => (
                        <span key={index} className="px-3 py-1 bg-[#F5F8FA] text-[#202356] rounded-full text-sm font-medium">
                          {country}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-base font-medium text-gray-900">Not provided</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">Preferred Cities</p>
                  {formData.preferredCities && formData.preferredCities.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.preferredCities.map((city, index) => (
                        <span key={index} className="px-3 py-1 bg-[#F5F8FA] text-[#202356] rounded-full text-sm font-medium">
                          {city}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-base font-medium text-gray-900">Not provided</p>
                  )}
                </div>
              </div>
            </div>

            {/* Programme Choices */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Programme Choices</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Your Programme & Institution Preferences</p>
                  {formData.programmeChoices && formData.programmeChoices.length > 0 ? (
                    <div className="space-y-3">
                      {formData.programmeChoices.map((choice, index) => (
                        <div key={index} className="p-3 bg-[#F5F8FA] rounded-lg border border-gray-200">
                          <p className="font-semibold text-[#202356]">{choice.programme}</p>
                          <p className="text-sm text-gray-600 mt-1">at {choice.institution}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-base font-medium text-gray-900">Not provided</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Budget Range</p>
                  <p className="text-base font-medium text-gray-900">{formData.budgetRange || 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Additional Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Preferences</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded ${formData.scholarshipInterest ? 'bg-green-500' : 'bg-gray-300'}`}>
                    {formData.scholarshipInterest && (
                      <CheckCircle className="text-white" size={20} />
                    )}
                  </div>
                  <span className="text-base text-gray-900">Interested in scholarships</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded ${formData.accommodationNeeded ? 'bg-green-500' : 'bg-gray-300'}`}>
                    {formData.accommodationNeeded && (
                      <CheckCircle className="text-white" size={20} />
                    )}
                  </div>
                  <span className="text-base text-gray-900">Accommodation needed</span>
                </div>
                {formData.additionalPreferences && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Other Preferences</p>
                    <p className="text-base font-medium text-gray-900">{formData.additionalPreferences}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Study Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Study Preferences</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Level of Study *
                  </label>
                  <select
                    name="levelOfStudy"
                    value={formData.levelOfStudy}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  >
                    <option value="">Select level</option>
                    <option value="undergraduate">Undergraduate</option>
                    <option value="postgraduate_taught">Postgraduate (Taught)</option>
                    <option value="postgraduate_research">Postgraduate (Research)</option>
                    <option value="phd">PhD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field of Study *
                  </label>
                  <input
                    type="text"
                    name="fieldOfStudy"
                    value={formData.fieldOfStudy}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Computer Science"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Study Mode *
                  </label>
                  <select
                    name="studyMode"
                    value={formData.studyMode}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  >
                    <option value="">Select mode</option>
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="online">Online</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Intake Preference
                  </label>
                  <select
                    name="intakePreference"
                    value={formData.intakePreference}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  >
                    <option value="">Select intake</option>
                    <option value="january">January</option>
                    <option value="september">September</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Location Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Location Preferences</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Countries
                  </label>
                  <div className="flex gap-2 mb-2">
                    <select
                      value={countryInput}
                      onChange={(e) => setCountryInput(e.target.value)}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                    >
                      <option value="">Select a country</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={addCountry}
                      className="px-4 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {formData.preferredCountries.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.preferredCountries.map((country, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-[#F5F8FA] text-[#202356] rounded-full text-sm font-medium flex items-center gap-2"
                        >
                          {country}
                          <button
                            type="button"
                            onClick={() => removeCountry(country)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Cities
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={cityInput}
                      onChange={(e) => setCityInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCity())}
                      placeholder="Enter city name"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                    />
                    <button
                      type="button"
                      onClick={addCity}
                      className="px-4 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {formData.preferredCities.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.preferredCities.map((city, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-[#F5F8FA] text-[#202356] rounded-full text-sm font-medium flex items-center gap-2"
                        >
                          {city}
                          <button
                            type="button"
                            onClick={() => removeCity(city)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Programme Choices */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Programme Choices</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Programme & Institution
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      value={newProgrammeChoice.programme}
                      onChange={(e) => setNewProgrammeChoice(prev => ({ ...prev, programme: e.target.value }))}
                      placeholder="Programme (e.g., MSc Computer Science)"
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                    />
                    <input
                      type="text"
                      value={newProgrammeChoice.institution}
                      onChange={(e) => setNewProgrammeChoice(prev => ({ ...prev, institution: e.target.value }))}
                      placeholder="Institution (e.g., Harvard University)"
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addProgrammeChoice}
                    disabled={!newProgrammeChoice.programme || !newProgrammeChoice.institution}
                    className="w-full px-4 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Programme Choice
                  </button>
                  
                  {formData.programmeChoices.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {formData.programmeChoices.map((choice, index) => (
                        <div key={index} className="p-3 bg-[#F5F8FA] rounded-lg border border-gray-200 flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-[#202356]">{choice.programme}</p>
                            <p className="text-sm text-gray-600 mt-1">at {choice.institution}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeProgrammeChoice(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget Range
                  </label>
                  <input
                    type="text"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleChange}
                    placeholder="e.g., $20,000 - $30,000 per year"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  />
                </div>
              </div>
            </div>

            {/* Additional Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Preferences</h2>
              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="scholarshipInterest"
                    checked={formData.scholarshipInterest}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                  />
                  <span className="text-sm text-gray-700">I am interested in scholarships</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="accommodationNeeded"
                    checked={formData.accommodationNeeded}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                  />
                  <span className="text-sm text-gray-700">I need accommodation assistance</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Other Preferences
                  </label>
                  <textarea
                    name="additionalPreferences"
                    value={formData.additionalPreferences}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Any other preferences or requirements..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X size={20} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
      />
    </div>
  );
};

export default ProgrammePreferencesSection;
